import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

const INTENT_LABELS = { buy: "Купівля", service: "Послуга", local: "Локальний", review: "Огляд", info: "Інфо" };
const INTENT_COLORS = { buy: "#3b82f6", service: "#8b5cf6", local: "#f59e0b", review: "#10b981", info: "#6b7280" };
const INTENT_BG = { buy: "rgba(59,130,246,0.1)", service: "rgba(139,92,246,0.1)", local: "rgba(245,158,11,0.1)", review: "rgba(16,185,129,0.1)", info: "rgba(107,114,128,0.1)" };

export default function AIVisibilityView() {
  const { proj, activeId, apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, stitle, muted } = useApp();
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [checking, setChecking] = useState(false);
  const [progress, setProgress] = useState(null);
  // 2-step flow
  const [prepared, setPrepared] = useState(null); // null = not prepared, [] = prepared items
  const [preparedSavedAt, setPreparedSavedAt] = useState(null);
  const [showPrepared, setShowPrepared] = useState(false); // expand prepared editor on demand
  const saveTimerRef = useState({ current: null })[0];
  const [queryVariants, setQueryVariants] = useState({}); // keyword -> [q1, q2, q3]
  const [expandingQueries, setExpandingQueries] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [savingNow, setSavingNow] = useState(false);

  const expandQueries = async () => {
    if (!prepared || !prepared.length) return;
    setExpandingQueries(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ai-visibility/expand-queries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keywords: prepared.map(p => p.keyword) }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); flash("Помилка: " + (e.error || "не вдалося")); setExpandingQueries(false); return; }
      const d = await r.json();
      const map = {};
      (d.items || []).forEach(it => { if (it.keyword && Array.isArray(it.queries)) map[it.keyword] = it.queries; });
      setQueryVariants(map);
      flash("Згенеровано варіанти запитів");
    } catch (e) { flash("Помилка: " + e.message); }
    setExpandingQueries(false);
  };
  const [importingSerp, setImportingSerp] = useState(false);
  // Підтягнути запити з Serpstat (related / competitors / suggestions) і влити в набір «Підготувати».
  const importSerpstat = async (type) => {
    if (!type) return;
    const cur = Array.isArray(prepared) ? prepared : [];
    if (cur.length && !window.confirm(`Замінити поточний набір (${cur.length}) запитами з Serpstat? Поточні запити буде видалено.`)) return;
    setImportingSerp(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ai-visibility/import-serpstat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, limit: 40 }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) { flash("Serpstat: " + (d.error || "не вдалося")); setImportingSerp(false); return; }
      const seen = new Set();
      const clean = [];
      (d.items || []).forEach(it => {
        const k = (it.keyword || "").toLowerCase().trim();
        if (!k || seen.has(k)) return;
        seen.add(k);
        clean.push({ keyword: it.keyword, query: it.query || it.keyword, intent: it.intent || "info" });
      });
      setPrepared(clean);
      setShowPrepared(true);
      flash(clean.length ? `Serpstat: ${clean.length} запитів (${type})` : "Serpstat: запитів не знайдено");
    } catch (e) { flash("Помилка: " + e.message); }
    setImportingSerp(false);
  };

  const [clearingAll, setClearingAll] = useState(false);
  // Видалити всі ключі, запити та історію перевірок AI-видимості для проекту.
  const clearAll = async () => {
    if (!window.confirm("Видалити ВСІ ключі, запити та історію перевірок AI-видимості для цього проекту? Дію не можна скасувати.")) return;
    setClearingAll(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ai-visibility/clear-all`, { method: "DELETE" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) { flash("Помилка: " + (d.error || "не вдалося")); setClearingAll(false); return; }
      setResults([]); setHistory([]); setPrepared(null); setPreparedSavedAt(null); setQueryVariants({}); setShowPrepared(false);
      flash(`Очищено: ${d.deletedResults || 0} записів історії, ${d.deletedQueries || 0} запитів`);
    } catch (e) { flash("Помилка: " + e.message); }
    setClearingAll(false);
  };

  const token = localStorage.getItem("metago_token");

  const load = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ai-visibility`);
      const d = await r.json();
      if (d.results) setResults(d.results);
      if (d.history) setHistory(d.history);
    } catch {}
  };

  useEffect(() => { if (activeId) load(); }, [activeId]);

  // Load saved prepared set on mount
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/ai-visibility/prepared`)
      .then(r => r.json())
      .then(d => {
        if (d?.items?.length) { setPrepared(d.items); setPreparedSavedAt(d.savedAt); }
      })
      .catch(() => {});
  }, [activeId]);

  // Auto-save prepared on change (debounced 600ms)
  useEffect(() => {
    if (!activeId || !Array.isArray(prepared)) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      apiFetch(`/api/projects/${activeId}/ai-visibility/prepared`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: prepared }),
      }).then(r => r.json()).then(d => { if (d?.ok) setPreparedSavedAt(new Date().toISOString()); }).catch(() => {});
    }, 600);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [prepared, activeId]);

  const resetPrepared = async () => {
    if (!confirm("Скинути збережений набір запитів і згенерувати наново?")) return;
    try {
      await apiFetch(`/api/projects/${activeId}/ai-visibility/prepared`, { method: "DELETE" });
      setPrepared(null);
      setPreparedSavedAt(null);
      flash("Збережений набір видалено");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  // Step 1: Prepare keywords
  // Edit prepared item
  const updatePrepared = (idx, field, value) => {
    setPrepared(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const removePrepared = (idx) => {
    setPrepared(prev => prev.filter((_, i) => i !== idx));
  };

  // Bulk add keywords (one per line / comma-separated)
  const addBulk = () => {
    const lines = bulkText.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    if (!lines.length) return;
    setPrepared(prev => {
      const base = Array.isArray(prev) ? prev : [];
      const existing = new Set(base.map(p => (p.keyword || "").toLowerCase().trim()));
      const add = [];
      lines.forEach(kw => {
        const k = kw.toLowerCase();
        if (existing.has(k)) return;
        existing.add(k);
        add.push({ keyword: kw, intent: "info", query: kw });
      });
      return [...base, ...add];
    });
    setBulkText("");
    flash(`Додано ключів: ${lines.length}`);
  };

  // Explicit save (immediate PUT)
  const saveNow = async () => {
    if (!activeId || !Array.isArray(prepared)) return;
    setSavingNow(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ai-visibility/prepared`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: prepared }),
      });
      const d = await r.json();
      if (d?.ok) { setPreparedSavedAt(new Date().toISOString()); flash("Збережено ✓"); }
      else flash("Помилка збереження");
    } catch (e) { flash("Помилка: " + e.message); }
    setSavingNow(false);
  };

  // Step 2: Run check with prepared items
  const runCheck = async () => {
    setChecking(true);
    const items = prepared || [];
    setProgress({ current: 0, total: items.length, keyword: "" });
    setResults([]);
    try {
      const resp = await fetch(`/api/projects/${activeId}/ai-visibility/check`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!resp.ok) {
        const d = await resp.json();
        flash(d.error || "Error");
        setChecking(false);
        setProgress(null);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      const newResults = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const msg = JSON.parse(line.slice(6));
            if (msg.type === "progress") setProgress(msg);
            else if (msg.type === "result") {
              newResults.push(msg);
              setResults([...newResults]);
            } else if (msg.type === "done") {
              flash(`Перевірено: ${msg.citedCount}/${msg.total} цитувань`);
            }
          } catch {}
        }
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setChecking(false);
    setProgress(null);
    // Keep prepared — saved set persists for next runs
    load();
  };

  const cited = results.filter(r => r.cited);
  const total = results.length;
  const pct = total > 0 ? Math.round((cited.length / total) * 100) : 0;

  // Per-source stats
  const sourceStats = useMemo(() => {
    const map = {};
    results.forEach(r => {
      if (!map[r.source]) map[r.source] = { cited: 0, total: 0 };
      map[r.source].total++;
      if (r.cited) map[r.source].cited++;
    });
    return Object.entries(map).map(([src, s]) => ({
      source: src,
      cited: s.cited,
      total: s.total,
      pct: s.total > 0 ? Math.round((s.cited / s.total) * 100) : 0,
    }));
  }, [results]);

  // Group results by keyword (one row per keyword, columns per source)
  const groupedResults = useMemo(() => {
    const map = new Map();
    results.forEach(r => {
      const key = r.keyword;
      if (!map.has(key)) map.set(key, { keyword: r.keyword, intent: r.intent, query: r.query, competitors: r.competitors || [], sources: {} });
      map.get(key).sources[r.source] = { cited: r.cited, position: r.position, snippet: r.snippet || "" };
      // Merge competitors from all sources
      (r.competitors || []).forEach(c => {
        if (!map.get(key).competitors.find(x => x.domain === c.domain)) map.get(key).competitors.push(c);
      });
    });
    return [...map.values()];
  }, [results]);

  const sourceList = useMemo(() => {
    const set = new Set();
    results.forEach(r => set.add(r.source));
    return [...set];
  }, [results]);

  const sourceLabels = { claude: "Claude", openai: "ChatGPT", gemini: "Gemini", chatgpt: "ChatGPT", perplexity: "Perplexity", tavily: "Search" };

  // Сортування рядків по моделі: спочатку де сайт присутній (за позицією), потім де нема. Клік по заголовку.
  const [sortSrc, setSortSrc] = useState(null);
  const [answerModal, setAnswerModal] = useState(null);
  const sortedResults = useMemo(() => {
    if (!sortSrc) return groupedResults;
    const rank = (g) => (g.sources[sortSrc] && g.sources[sortSrc].cited ? 0 : 1);
    const pos = (g) => (g.sources[sortSrc] && g.sources[sortSrc].position) || 999;
    return [...groupedResults].sort((a, b) => rank(a) - rank(b) || pos(a) - pos(b));
  }, [groupedResults, sortSrc]);

  const competitorStats = useMemo(() => {
    const map = new Map();
    results.forEach(r => {
      (r.competitors || []).forEach(c => {
        const d = c.domain;
        if (!map.has(d)) map.set(d, { domain: d, count: 0 });
        map.get(d).count++;
      });
    });
    return [...map.values()].sort((a, b) => b.count - a.count).slice(0, 10);
  }, [results]);

  if (!proj) return null;

  const barColor = pct >= 50 ? "#10b981" : pct >= 25 ? "#f59e0b" : "#ef4444";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>AI Видимість</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {!checking && (
            <select value="" disabled={importingSerp}
              onChange={e => { const v = e.target.value; e.target.value = ""; importSerpstat(v); }}
              title="Підтягнути запити з Serpstat (вопросы по ключам / конкурентов / автоподсказки)"
              style={{ ...inp, width: 176, fontSize: 12, padding: "6px 8px" }}>
              <option value="" disabled>{importingSerp ? "⏳ Serpstat…" : "➕ З Serpstat"}</option>
              <option value="related">Питання по ключах</option>
              <option value="competitors">Питання конкурентів</option>
              <option value="suggestions">Автопідказки</option>
            </select>
          )}
          {!checking && (prepared || total > 0 || history.length > 0) && (
            <button onClick={clearAll} disabled={clearingAll}
              title="Видалити всі ключі, запити та історію перевірок AI-видимості"
              style={{ ...btnSm, fontSize: 12, color: "#ef4444", borderColor: "#ef444433", opacity: clearingAll ? 0.6 : 1 }}>
              {clearingAll ? "⏳ Очищаю…" : "🗑 Очистити все"}
            </button>
          )}
          {!prepared && !checking && (
            <button onClick={() => { setPrepared([]); setShowPrepared(true); }} style={{ ...btnP, fontSize: 12 }}>
              ✏ Додати вручну
            </button>
          )}
          {prepared && !checking && (
            <>
              <button onClick={() => setShowPrepared(s => !s)} style={{ ...btnSm, fontSize: 12 }}>
                {showPrepared ? "Сховати редактор" : `✏ Редагувати запити (${prepared.length})`}
              </button>
              {showPrepared && (
                <button onClick={expandQueries} disabled={expandingQueries || !prepared.length} style={{ ...btnG, fontSize: 12, opacity: expandingQueries ? 0.6 : 1 }}>
                  {expandingQueries ? "⏳ Генерую..." : "🤖 Запропонувати запити"}
                </button>
              )}
              <button onClick={runCheck} style={{ ...btnP, fontSize: 12 }}>
                Перевірити ({prepared.length})
              </button>
            </>
          )}
          {checking && (
            <span style={{ fontSize: 12, color: "#6366f1", fontWeight: 600 }}>
              Перевіряю {progress?.current || 0}/{progress?.total || 0}...
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {checking && progress && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>
            <strong>{progress.keyword}</strong>
            {progress.query ? <span style={{ marginLeft: 6, color: "#6366f1", fontSize: 10 }}>&rarr; {progress.query}</span> : ""}
          </div>
          <div style={{ height: 4, borderRadius: 2, background: dark ? "#333" : "#e5e7eb", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(progress.current / progress.total) * 100}%`, background: "#6366f1", borderRadius: 2, transition: "width .3s" }} />
          </div>
        </div>
      )}

      {/* Step 1: Prepared keywords table — only when explicitly toggled OR no results yet */}
      {prepared && !checking && (showPrepared || total === 0) && (
        <div style={{ ...card, padding: 0, overflow: "auto", marginBottom: 16 }}>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Крок 1: Перевірте запити</span>
            <span style={{ fontSize: 11, color: t.textMuted }}>— відредагуйте або видаліть зайві (зміни автозберігаються)</span>
            {preparedSavedAt && <span style={{ fontSize: 10, color: "#10b981", marginLeft: 4 }} title={"Останнє збереження: " + new Date(preparedSavedAt).toLocaleString("uk-UA")}>✓ збережено</span>}
            <span style={{ flex: 1 }} />
            <button onClick={saveNow} disabled={savingNow} title="Зберегти зміни зараз"
              style={{ fontSize: 11, padding: "4px 12px", borderRadius: 6, border: "none", background: "linear-gradient(135deg,#6366f1,#a855f7)", color: "#fff", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", opacity: savingNow ? 0.6 : 1 }}>{savingNow ? "..." : "💾 Зберегти"}</button>
            <button onClick={resetPrepared} title="Видалити збережений набір і згенерувати з 0"
              style={{ ...btnSm, fontSize: 11, padding: "4px 10px" }}>↻ Регенерувати</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}`, width: 30 }}>#</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}` }}>Ключ</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}`, width: 100 }}>Інтент</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}` }}>Запит для AI</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}`, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {prepared.map((item, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") }}>
                  <td style={{ padding: "6px 12px", fontSize: 11, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>{i + 1}</td>
                  <td style={{ padding: "6px 12px", borderBottom: `1px solid ${t.border}` }}>
                    <input type="text" value={item.keyword || ""} onChange={e => updatePrepared(i, "keyword", e.target.value)}
                      style={{ ...inp, width: "100%", fontSize: 13, fontWeight: 500, padding: "4px 8px" }} />
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: `1px solid ${t.border}` }}>
                    <select
                      value={item.intent}
                      onChange={e => {
                        updatePrepared(i, "intent", e.target.value);
                      }}
                      style={{ fontSize: 10, padding: "2px 4px", border: `1px solid ${t.border}`, borderRadius: 4, background: INTENT_BG[item.intent] || INTENT_BG.info, color: INTENT_COLORS[item.intent] || INTENT_COLORS.info, fontWeight: 600, cursor: "pointer" }}
                    >
                      {Object.entries(INTENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "6px 12px", borderBottom: `1px solid ${t.border}` }}>
                    <input
                      type="text"
                      value={item.query}
                      onChange={e => updatePrepared(i, "query", e.target.value)}
                      style={{ ...inp, width: "100%", fontSize: 12, padding: "4px 8px" }}
                    />
                    {queryVariants[item.keyword] && queryVariants[item.keyword].length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                        {queryVariants[item.keyword].map((q, qi) => (
                          <button key={qi} onClick={() => updatePrepared(i, "query", q)}
                            style={{
                              fontSize: 10, padding: "2px 8px", borderRadius: 4,
                              border: `1px solid ${item.query === q ? "#6366f1" : t.border}`,
                              background: item.query === q ? "rgba(99,102,241,0.12)" : "transparent",
                              color: item.query === q ? "#6366f1" : t.textMuted,
                              cursor: "pointer", fontFamily: "inherit", textAlign: "left", lineHeight: 1.3,
                            }}
                            title="Клік — підставити в запит">
                            {q}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "6px 12px", textAlign: "center", borderBottom: `1px solid ${t.border}` }}>
                    <button onClick={() => removePrepared(i)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#ef4444", padding: 0 }} title="Видалити">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "12px 18px", borderTop: `1px solid ${t.border}`, display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
            <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} placeholder="Додати ключі списком — по одному в рядку або через кому" rows={2}
              style={{ ...inp, flex: 1, minWidth: 240, fontSize: 12, padding: "6px 10px", resize: "vertical", fontFamily: "inherit" }} />
            <button onClick={addBulk} disabled={!bulkText.trim()}
              style={{ fontSize: 12, padding: "8px 16px", borderRadius: 6, border: `1px solid ${t.border}`, background: bulkText.trim() ? "#6366f1" : "transparent", color: bulkText.trim() ? "#fff" : t.textMuted, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ Додати ключі</button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {total > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ ...card, flex: 1, minWidth: 150, padding: 14, marginBottom: 0 }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 4 }}>AI ВИДИМІСТЬ</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: barColor }}>{pct}%</div>
            <div style={{ height: 6, borderRadius: 3, background: dark ? "#333" : "#e5e7eb", marginTop: 8, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>{cited.length} з {total} запитів (загалом)</div>
              {sourceStats.length > 0 && (
                <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                  {sourceStats.map(ss => {
                    const lbl = (typeof sourceLabels !== 'undefined' && sourceLabels[ss.source]) || ss.source;
                    const c = ss.pct >= 50 ? "#10b981" : ss.pct >= 25 ? "#f59e0b" : "#ef4444";
                    return (
                      <div key={ss.source} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, padding: "4px 10px", borderRadius: 6, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", border: `1px solid ${t.border}` }}>
                        <span style={{ color: t.textMuted }}>{lbl}:</span>
                        <span style={{ fontWeight: 700, color: c }}>{ss.pct}%</span>
                        <span style={{ color: t.textMuted, fontSize: 10 }}>({ss.cited}/{ss.total})</span>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>

          <div style={{ ...card, flex: 1, minWidth: 150, padding: 14, marginBottom: 0 }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 4 }}>КОНКУРЕНТИ В AI</div>
            {competitorStats.slice(0, 5).map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>
                <span style={{ fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.domain}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#6366f1" }}>{c.count}/{total}</span>
              </div>
            ))}
            {competitorStats.length === 0 && <div style={{ fontSize: 11, color: t.textMuted }}>—</div>}
          </div>

          {history.length > 1 && (
            <div style={{ ...card, flex: 1, minWidth: 150, padding: 14, marginBottom: 0 }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 4 }}>ДИНАМІКА</div>
              {history.slice(0, 6).map((h, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>
                  <span style={{ fontSize: 10, color: t.textMuted, width: 70 }}>{new Date(h.date).toLocaleDateString("uk-UA")}</span>
                  <div style={{ flex: 1, height: 4, borderRadius: 2, background: dark ? "#333" : "#e5e7eb", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.round((h.cited_count / h.total_count) * 100)}%`, background: "#6366f1", borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, width: 30, textAlign: "right" }}>{Math.round((h.cited_count / h.total_count) * 100)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results table */}
      {total > 0 && (
        <div style={{ ...card, padding: 0, overflow: "auto" }}>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Результати перевірки</span>
            {results[0]?.checked_at && (
              <span style={{ fontSize: 11, color: t.textMuted, marginLeft: "auto" }}>
                {new Date(results[0].checked_at).toLocaleString("uk-UA")}
              </span>
            )}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}` }}>Ключ</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}`, width: 80 }}>Інтент</th>
                {sourceList.map(src => (
                  <th key={src} onClick={() => setSortSrc(cur => cur === src ? null : src)}
                      title="Сортувати: спочатку запити, де сайт присутній у цій моделі"
                      style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, fontWeight: 600, color: sortSrc === src ? "#10b981" : t.textMuted, borderBottom: `2px solid ${sortSrc === src ? "#10b981" : t.border}`, width: 90, cursor: "pointer", userSelect: "none" }}>
                    {sourceLabels[src] || src}{sortSrc === src ? " \u25be" : " \u21c5"}
                  </th>
                ))}
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: t.textMuted, borderBottom: `2px solid ${t.border}` }}>Конкуренти у відповідях</th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((g, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") }}>
                  <td style={{ padding: "8px 12px", fontSize: 13, borderBottom: `1px solid ${t.border}` }} title={g.query || ""}>{g.keyword}</td>
                  <td style={{ padding: "8px 12px", textAlign: "center", borderBottom: `1px solid ${t.border}` }}>
                    {g.intent && <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: INTENT_BG[g.intent] || INTENT_BG.info, color: INTENT_COLORS[g.intent] || INTENT_COLORS.info }}>{INTENT_LABELS[g.intent] || g.intent}</span>}
                  </td>
                  {sourceList.map(src => {
                    const sr = g.sources[src];
                    if (!sr) return <td key={src} style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>—</td>;
                    return (
                      <td key={src} onClick={() => sr.snippet && setAnswerModal({ src, keyword: g.keyword, query: g.query, snippet: sr.snippet })}
                          title={sr.snippet ? "Показати повний текст відповіді моделі" : ""}
                          style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, fontWeight: 700, borderBottom: `1px solid ${t.border}`, color: sr.cited ? "#10b981" : "#ef4444", cursor: sr.snippet ? "pointer" : "default", textDecoration: sr.snippet ? "underline dotted" : "none" }}>
                        {sr.cited ? `#${sr.position || "?"}` : "—"}
                      </td>
                    );
                  })}
                  <td style={{ padding: "8px 12px", fontSize: 11, borderBottom: `1px solid ${t.border}`, color: t.textMuted }}>
                    {(g.competitors || []).slice(0, 4).map(c => c.domain).join(", ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {answerModal && (
        <div onClick={() => setAnswerModal(null)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px", overflowY: "auto" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#1a1a2e" : "#fff", border: `1px solid ${t.border}`, borderRadius: 14, maxWidth: 760, width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 80px rgba(0,0,0,0.5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "18px 22px", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{(sourceLabels[answerModal.src] || answerModal.src) + " · текст відповіді"}</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{answerModal.keyword}</div>
                {answerModal.query && answerModal.query !== answerModal.keyword && <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>{"Запит: " + answerModal.query}</div>}
              </div>
              <button onClick={() => setAnswerModal(null)} style={{ ...btnG, height: 30, flexShrink: 0 }}>✕ Закрити</button>
            </div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.65, color: t.text, padding: "16px 22px", overflowY: "auto", flex: 1, minHeight: 0 }}>
              {answerModal.snippet || "(модель не повернула тексту)"}
            </div>
          </div>
        </div>
      )}

      {total === 0 && !checking && !prepared && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Перевірте видимість сайту в AI</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16 }}>
            Додайте запити з <b>Serpstat</b> (кнопка «➕ З Serpstat» вгорі) або вручну — Spalah перевірить, чи цитується ваш сайт у відповідях AI
          </p>
          <button onClick={() => { setPrepared([]); setShowPrepared(true); }} style={btnP}>✏ Додати вручну</button>
        </div>
      )}
    </div>
  );
}
